// @ts-nocheck
import { useEffect, useState } from 'react'
import { UserSquare, X, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

export default function TaskRoutingSettings({ phoneNumber, onClose }) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [groups, setGroups] = useState([])
  const [taskRouting, setTaskRouting] = useState({
    enabled: false,
    groupId: null,
  })

  // グループリストとタスクルーティング設定を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // グループリスト取得
        const groupsData = await api.get('/api/groups')
        setGroups(groupsData.items || [])

        // タスクルーティング設定取得
        const routingData = await api.get(`/api/phone-numbers/${phoneNumber.phoneNumberId}/task-routing`)
        const tr = routingData.taskRouting
        if (tr) {
          setTaskRouting({
            enabled: tr.enabled === true,
            groupId: tr.groupId ?? null,
          })
        }
      } catch (err) {
        console.error('データ取得エラー:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (phoneNumber?.phoneNumberId) {
      fetchData()
    }
  }, [phoneNumber?.phoneNumberId, api])

  // タスクルーティング設定を保存
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}/task-routing`, {
        enabled: taskRouting.enabled,
        groupId: taskRouting.enabled ? taskRouting.groupId : null,
      })

      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        onClose()
      }, 1500)
    } catch (err) {
      console.error('保存エラー:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
            <UserSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              タスクルーティング設定
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {phoneNumber.name} ({formatPhoneNumberForDisplay(phoneNumber.phoneNumber)})
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* エラーメッセージ */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* 保存成功メッセージ */}
      {saved && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            設定を保存しました
          </p>
        </div>
      )}

      {/* 説明 */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>タスクルーティング</strong>を有効にすると、この電話番号への着信を特定のグループに所属するオペレーターのみに通知できます。
          無効の場合は、すべてのオンラインオペレーターに通知されます。
        </p>
      </div>

      {/* タスクルーティング有効/無効 */}
      <div className="mb-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">
              タスクルーティングを有効にする
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              特定のグループに着信を振り分けます
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={taskRouting.enabled}
              onChange={(e) =>
                setTaskRouting({ ...taskRouting, enabled: e.target.checked })
              }
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
          </label>
        </div>
      </div>

      {/* グループ選択 */}
      {taskRouting.enabled && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            ターゲットグループ <span className="text-red-500">*</span>
          </label>
          {groups.length === 0 ? (
            <div className="p-6 bg-gray-50 dark:bg-gray-700/50 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
              <UserSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                グループが登録されていません
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                グループ管理ページからグループを作成してください
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => (
                <label
                  key={group.groupId}
                  className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    taskRouting.groupId === group.groupId
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 bg-white dark:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="groupId"
                    value={group.groupId}
                    checked={taskRouting.groupId === group.groupId}
                    onChange={(e) =>
                      setTaskRouting({ ...taskRouting, groupId: e.target.value })
                    }
                    className="w-4 h-4 text-purple-600 focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {group.groupName}
                      </p>
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded">
                        {group.memberCount || 0}人
                      </span>
                    </div>
                    {group.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {group.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 保存ボタン */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-5 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 font-medium transition-colors"
        >
          キャンセル
        </button>
        <button
          onClick={handleSave}
          disabled={saving || (taskRouting.enabled && !taskRouting.groupId)}
          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  )
}
